/**
 * Multilingual Audio ZIP Strategy
 *
 * Creates ZIP archive with all generated audio tracks in multiple languages.
 * Implements IZipContentStrategy for extensibility.
 */

import type { IZipContentStrategy, ZipFileEntry } from '../types';
import type { AudioGenerationResponse } from '@/types';

/**
 * Strategy for creating ZIP with multilingual audio files.
 * Useful for users who generate audio narration in multiple languages.
 */
export class MultilingualAudioStrategy implements IZipContentStrategy {
  readonly type = 'multilingual_audio';

  /**
   * Build file entries from audio generation response.
   *
   * @param sourceData - AudioGenerationResponse from audio generation API
   * @returns Array of file entries for ZIP
   */
  buildFileEntries(sourceData: unknown): ZipFileEntry[] {
    const response = sourceData as AudioGenerationResponse;
    const entries: ZipFileEntry[] = [];

    // Add primary audio file - simple naming: {lang}.mp3
    if (response.audio_url) {
      const primaryLang = (response.metadata?.language as string) || 'primary';
      entries.push({
        url: response.audio_url,
        filename: `${primaryLang}.mp3`,
        metadata: {
          type: 'audio',
          language: primaryLang,
          format: 'mp3',
          primary: true,
          duration: response.duration,
        },
      });
    }

    // Add multilingual audio files - all in root with simple naming: {lang}.mp3
    if (response.multilingual_audio_urls && typeof response.multilingual_audio_urls === 'object') {
      const primaryLang = (response.metadata?.language as string) || 'primary';

      for (const [langCode, url] of Object.entries(response.multilingual_audio_urls)) {
        if (url) {
          // Skip if this is the same language as the primary audio (avoid duplicates)
          if (langCode === primaryLang) continue;

          entries.push({
            url: url as string,
            filename: `${langCode}.mp3`,
            // No folder - all files in root for simple user experience
            metadata: {
              type: 'audio',
              language: langCode,
              format: 'mp3',
              primary: false,
            },
          });
        }
      }
    }

    return entries;
  }

  /**
   * Get archive name based on generation ID.
   */
  getArchiveName(sourceData: unknown): string {
    const response = sourceData as AudioGenerationResponse;

    if (response.generation_id) {
      return `audio_multilingual_${response.generation_id}`;
    }

    return `audio_multilingual_${Date.now()}`;
  }

  /**
   * Generate manifest data for the ZIP.
   */
  getManifestData(sourceData: unknown): Record<string, unknown> {
    const response = sourceData as AudioGenerationResponse;
    const languages = response.audio_languages || [];
    const primaryLang = (response.metadata?.language as string) || 'unknown';

    return {
      type: 'multilingual_audio_package',
      audio: {
        primaryLanguage: primaryLang,
        duration: response.duration,
        wordCount: response.word_count,
        fileSize: response.file_size,
      },
      languages: {
        count: languages.length + 1, // +1 for primary
        available: [primaryLang, ...languages.filter((l) => l !== primaryLang)],
      },
      metadata: {
        ttsProvider: response.metadata?.tts_provider,
        voiceId: response.metadata?.voice_id,
        mode: response.metadata?.mode,
      },
      generatedAt: new Date().toISOString(),
      generationId: response.generation_id,
    };
  }
}

// Export singleton for convenience
export const multilingualAudioStrategy = new MultilingualAudioStrategy();
